/**
 * Módulo `numberOptions`
 *
 * Contiene funciones utilitarias para la **validación y conversión de valores numéricos**.
 * Proporciona tres niveles de manejo numérico: validación pura, conversión permisiva
 * (para inputs controlados) y conversión estricta (para payloads de backend).
 *
 * **Funciones principales:**
 * - `isValidNumber` — Valida si un valor es un número finito válido (string o number).
 * - `validNumber` — Convierte a número si es válido, devuelve el valor original si no (inputs).
 * - `getNumberValue` — Convierte a número estrictamente, retorna `undefined` si es inválido (APIs).
 * - `getRandomEcuadorianPhoneNumber` — Genera números de teléfono ecuatorianos de ejemplo.
 */

export type EcuadorianPhoneType = 'mobile' | 'landline';

/**
 * Valida si un valor puede ser interpretado como un número finito válido.
 *
 * Reglas:
 * - `null` y `undefined` → inválidos
 * - `number` → válido solo si no es `NaN` ni `Infinity`
 * - `string` → se recorta (`trim`) y se valida que:
 *   - no esté vacío
 *   - pueda convertirse a número
 *   - sea finito
 *
 * Casos aceptados:
 * - "123"
 * - " 45.67 "
 * - 0
 * - -10
 *
 * Casos rechazados:
 * - ""
 * - "   "
 * - "abc"
 * - NaN
 * - Infinity
 *
 * @param value Valor a evaluar
 * @returns `true` si el valor representa un número válido; `false` en caso contrario
 */
export function isValidNumber(value?: string | number | null): boolean {
    if (value === null || value === undefined) {
        return false;
    } else if (typeof value === 'number') {
        return !isNaN(value) && isFinite(value);
    } else {
        const trimmed = value.trim();
        return trimmed !== '' && !isNaN(Number(trimmed)) && isFinite(Number(trimmed));
    }
}

/**
 * Normaliza un valor numérico manteniendo compatibilidad con inputs controlados.
 *
 * Diferencia clave frente a `getNumberValue`:
 * - Si el valor NO es numéricamente válido, **lo devuelve sin modificar**
 *   (útil para no romper la escritura del usuario en inputs).
 *
 * Comportamiento:
 * - `null | undefined | ""` → `undefined`
 * - Valor no numérico válido → retorna el valor original
 * - Valor numérico válido → retorna `number`
 *
 * Uso recomendado:
 * - Inputs controlados
 * - Formularios donde el usuario aún está escribiendo
 *
 * @param value Valor de entrada
 * @returns Número convertido, valor original (si inválido), o `undefined`
 */
export function validNumber(value?: string | number | null): number | string | undefined {
    if (!value && value !== 0) {
        return undefined;
    }

    if (!isValidNumber(value)) {
        return value;
    }

    return typeof value === 'number' ? value : parseFloat(value);
}

/**
 * Convierte un valor a número **solo si es válido**.
 *
 * A diferencia de `validNumber`:
 * - Si el valor no es numéricamente válido → retorna `undefined`
 * - Nunca devuelve strings
 *
 * Uso recomendado:
 * - Payloads para backend
 * - Persistencia de datos
 * - Normalización antes de enviar a APIs
 *
 * Comportamiento:
 * - `null | undefined | ""` → `undefined`
 * - Valor no numérico → `undefined`
 * - Valor numérico válido → `number`
 *
 * @param value Valor de entrada
 * @returns Número convertido o `undefined`
 */
export function getNumberValue(value?: string | number | null): number | undefined {
    if (!value && value !== 0) {
        return undefined;
    }

    if (!isValidNumber(value)) {
        return undefined;
    }

    return typeof value === 'number' ? value : parseFloat(value);
}

/**
 * Genera un número de teléfono ecuatoriano aleatorio para usar como ejemplo.
 *
 * Tipos disponibles:
 * - `mobile`: formato local ecuatoriano `09XXXXXXXX` (10 dígitos)
 * - `landline`: formato local ecuatoriano `0AXXXXXXX` (9 dígitos)
 *   donde `A` representa un código de área entre 2 y 7.
 *
 * @param type Tipo de teléfono a generar (`mobile` por defecto)
 * @returns Número telefónico aleatorio en formato local ecuatoriano
 */
export function getRandomEcuadorianPhoneNumber(type: EcuadorianPhoneType = 'mobile'): string {
    if (type === 'landline') {
        const areaCode = (Math.floor(Math.random() * 6) + 2).toString();
        const localNumber = Math.floor(Math.random() * 10000000).toString().padStart(7, '0');
        return `0${areaCode}${localNumber}`;
    }

    const subscriberNumber = Math.floor(Math.random() * 100000000).toString().padStart(8, '0');
    return `09${subscriberNumber}`;
}
